import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'types';
import {
  filterPlacementsByQuickviewKey,
  filterPlacementsByGridFilters,
  filterPlacementsByBarChartMonth,
} from './recruitmentRetentionHelper';
import { IRecruitmentRetentionPlacement } from 'app/models/MyCandidates/RecruitmentRetention';

/** Stable empty references to avoid selector churn and unnecessary re-renders */
const EMPTY_DOMAIN = Object.freeze({});
const EMPTY_PLACEMENTS: IRecruitmentRetentionPlacement[] = [];
const EMPTY_ARRAY: unknown[] = [];
const EMPTY_GRID_FILTERS = Object.freeze({
  discipline: [] as any[],
  specialty: [] as any[],
  states: 'all',
  candidateStatus: [] as any[],
  rebooked: '',
  bookedOutOfRange: '',
  placementStatus: [] as any[],
});

const selectDomain = (state: RootState) => state?.myCandidates ?? EMPTY_DOMAIN;

export const selectMyCandidatesPlacements = createSelector(
  [selectDomain],
  domain => domain.placements ?? EMPTY_PLACEMENTS,
);

const getDiscipline = (p: IRecruitmentRetentionPlacement): string => {
  const d = (p.discipline ?? (p as any).Discipline ?? '').toString().trim();
  if (d) return d;
  const skillset = (p.skillset ?? (p as any).Skillset ?? '').toString();
  const parts = skillset.split(/[,-]/).map(s => s.trim()).filter(Boolean);
  return parts[0] ?? '';
};

const getSpecialty = (p: IRecruitmentRetentionPlacement): string => {
  const s = (p.specialty ?? (p as any).Specialty ?? '').toString().trim();
  if (s) return s;
  const skillset = (p.skillset ?? (p as any).Skillset ?? '').toString();
  const parts = skillset.split(/[,-]/).map(x => x.trim()).filter(Boolean);
  return parts[1] ?? '';
};

/** Unique disciplines from placements for filter dropdown options */
export const selectMyCandidatesDisciplineOptions = createSelector(
  [selectMyCandidatesPlacements],
  (placements: IRecruitmentRetentionPlacement[]) => {
    const unique = new Set<string>();
    placements.forEach(p => {
      const d = getDiscipline(p);
      if (d) unique.add(d);
    });
    const list = Array.from(unique).sort();
    return [
      { id: 0, name: `All Disciplines (${list.length})`, value: 'All' },
      ...list.map((s, i) => ({ id: i + 1, name: s, value: s })),
    ];
  },
);

/** Unique specialties from placements for filter dropdown options */
export const selectMyCandidatesSpecialtyOptions = createSelector(
  [selectMyCandidatesPlacements],
  (placements: IRecruitmentRetentionPlacement[]) => {
    const unique = new Set<string>();
    placements.forEach(p => {
      const s = getSpecialty(p);
      if (s) unique.add(s);
    });
    const list = Array.from(unique).sort();
    return [
      { id: 0, name: `All Specialties (${list.length})`, value: 'All' },
      ...list.map((s, i) => ({ id: i + 1, name: s, value: s })),
    ];
  },
);

/** Unique states from placements for filter dropdown options */
export const selectMyCandidatesStateOptions = createSelector(
  [selectMyCandidatesPlacements],
  (placements: IRecruitmentRetentionPlacement[]) => {
    const unique = new Set<string>();
    placements.forEach(p => {
      const s = (p.state ?? (p as any).State ?? '').toString().trim();
      if (s) unique.add(s);
    });
    const list = Array.from(unique).sort();
    return [
      { id: 0, name: `All States (${list.length})`, value: 'all' },
      ...list.map((s, i) => ({ id: i + 1, name: s, value: s })),
    ];
  },
);

/** Unique candidate statuses from placements for filter dropdown options */
export const selectMyCandidatesCandidateStatusOptions = createSelector(
  [selectMyCandidatesPlacements],
  (placements: IRecruitmentRetentionPlacement[]) => {
    const unique = new Set<string>();
    placements.forEach(p => {
      const s = (p.candidateStatus ?? (p as any).CandidateStatus ?? '').toString().trim();
      if (s && s !== '-') unique.add(s);
    });
    const list = Array.from(unique).sort();
    return [
      { id: 0, name: `All (${list.length})`, value: 'All' },
      ...list.map((s, i) => ({ id: i + 1, name: s, value: s })),
    ];
  },
);

/** Count of rebooked placements (rebooked='Y') */
export const selectMyCandidatesRebookedCount = createSelector(
  [selectMyCandidatesPlacements],
  (placements: IRecruitmentRetentionPlacement[]) =>
    placements.filter(
      p => ((p.rebooked ?? (p as any).Rebooked) ?? '').toString().toUpperCase() === 'Y',
    ).length,
);

/** Count of non-rebooked placements (rebooked!='Y') */
export const selectMyCandidatesNotRebookedCount = createSelector(
  [selectMyCandidatesPlacements],
  (placements: IRecruitmentRetentionPlacement[]) =>
    placements.filter(
      p => ((p.rebooked ?? (p as any).Rebooked) ?? '').toString().toUpperCase() !== 'Y',
    ).length,
);

/** Fiscal year boundaries for "out of range" logic (Apr 1 - Mar 31) */
const getFyBounds = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const fyStartYear = month >= 3 ? year : year - 1;
  const fyStart = new Date(fyStartYear, 3, 1);
  const fyEnd = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);
  return { fyStart, fyEnd };
};

const isBookedOutOfRange = (p: IRecruitmentRetentionPlacement): boolean => {
  const endDate = (p.currentEndDate ?? (p as any).CurrentEndDate ?? '') as string;
  if (!endDate || typeof endDate !== 'string') return true;
  const d = new Date(endDate);
  if (isNaN(d.getTime())) return true;
  const { fyStart, fyEnd } = getFyBounds();
  return d < fyStart || d > fyEnd;
};

/** Count of placements with end date outside fiscal year or null */
export const selectMyCandidatesBookedOutOfRangeCount = createSelector(
  [selectMyCandidatesPlacements],
  placements => placements.filter(isBookedOutOfRange).length,
);

/** Count of placements with end date within fiscal year */
export const selectMyCandidatesBookedInRangeCount = createSelector(
  [selectMyCandidatesPlacements],
  placements => placements.filter(p => !isBookedOutOfRange(p)).length,
);

export const selectMyCandidatesRecruiterId = createSelector(
  [selectDomain],
  domain => domain.recruiterId ?? null,
);

export const selectMyCandidatesBarChartData = createSelector(
  [selectDomain],
  domain => domain.barChartData ?? EMPTY_ARRAY,
);

/** Booked/rebooked totals for donut (segmented ring per UX) */
export const selectMyCandidatesDonutData = createSelector(
  [selectMyCandidatesPlacements],
  (placements: IRecruitmentRetentionPlacement[]) => {
    let booked = 0;
    let rebooked = 0;
    placements.forEach(p => {
      const isRebooked = ((p.rebooked ?? (p as any).Rebooked) ?? '').toString().toUpperCase() === 'Y';
      if (isRebooked) rebooked++;
      else booked++;
    });
    return { booked, rebooked };
  },
);

export const selectMyCandidatesQuickviewMetrics = createSelector(
  [selectDomain],
  domain => domain.quickviewMetrics ?? EMPTY_ARRAY,
);

export const selectMyCandidatesSelectedQuickviewKey = createSelector(
  [selectDomain],
  domain => domain.selectedQuickviewKey ?? null,
);

export const selectMyCandidatesSelectedBarChartMonth = createSelector(
  [selectDomain],
  domain => domain.selectedBarChartMonth ?? null,
);

export const selectMyCandidatesRecruiterOptions = createSelector(
  [selectDomain],
  domain => domain.recruiterOptions ?? EMPTY_ARRAY,
);

export const selectMyCandidatesDataLoaded = createSelector(
  [selectDomain],
  domain => domain.dataLoaded ?? false,
);

export const selectMyCandidatesGridFilters = createSelector(
  [selectDomain],
  domain => domain.gridFilters ?? EMPTY_GRID_FILTERS,
);

export const selectMyCandidatesGridData = createSelector(
  [
    selectMyCandidatesPlacements,
    selectMyCandidatesSelectedQuickviewKey,
    selectMyCandidatesSelectedBarChartMonth,
    selectMyCandidatesGridFilters,
  ],
  (placements, selectedKey, selectedBarChartMonth, gridFilters) => {
    const byQuickview = filterPlacementsByQuickviewKey(placements, selectedKey);
    const byBarChartMonth = filterPlacementsByBarChartMonth(byQuickview, selectedBarChartMonth);
    return filterPlacementsByGridFilters(byBarChartMonth, gridFilters);
  },
);
